import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { env } from '@/app/config/env';

import { logout as logoutRequest } from '../api/auth.api';
import { loadLastActivity, saveLastActivity } from '../api/last-activity';
import { getRefreshToken, useAuthStore } from '../store/auth.store';

/** Signed out after this long without any interaction (`VITE_IDLE_TIMEOUT_MINUTES`). */
export const IDLE_TIMEOUT_MS = env.idleTimeoutMinutes * 60 * 1000;

/** How often the idle clock is checked. */
const CHECK_INTERVAL_MS = 15 * 1000;

/** Activity is written to storage at most this often; mousemove fires constantly. */
const SAVE_THROTTLE_MS = 5 * 1000;

const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'wheel',
  'scroll',
  'touchstart',
] as const;

/**
 * Signs the user out after {@link IDLE_TIMEOUT_MS} with no interaction in any
 * tab. UI-only: requests made in the background (polling, refetch on focus) do
 * not count as activity.
 *
 * The clock is a wall-clock timestamp checked on an interval rather than a
 * single `setTimeout`, so a laptop that slept or a throttled background tab is
 * signed out as soon as it wakes. A session restored after the timeout has
 * already passed is ended on the first check.
 */
export function useIdleSignOut() {
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');
  const clear = useAuthStore((state) => state.clear);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) return;

    // No stored activity (first run, storage blocked): start the clock now.
    let lastActivity = loadLastActivity() ?? Date.now();
    let lastSaved = 0;

    const onActivity = () => {
      const now = Date.now();
      lastActivity = now;
      if (now - lastSaved >= SAVE_THROTTLE_MS) {
        lastSaved = now;
        saveLastActivity(now);
      }
    };

    const check = () => {
      // Another tab may have seen more recent activity.
      const latest = Math.max(lastActivity, loadLastActivity() ?? 0);
      if (Date.now() - latest < IDLE_TIMEOUT_MS) return;

      void logoutRequest(getRefreshToken());
      clear('idle');
      queryClient.clear();
    };

    check();
    const interval = window.setInterval(check, CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true, capture: true });
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(interval);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity, { capture: true });
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAuthenticated, clear, queryClient]);
}
